import {
  buildPlanEconomics,
  buildPurchasePlan,
  comparePurchasePlans,
} from './purchasePlanning';
import { convertBasisQuantity } from './packageBasis';

const finite = (value) => {
  if (value === null || value === undefined || value === '') return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const round6 = (value) => Math.round(value * 1e6) / 1e6;
const closeToZero = (value) => Math.abs(Number(value) || 0) < 0.005;

const money = (value) => Math.abs(Number(value) || 0).toLocaleString(undefined, {
  style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2,
});

const numberText = (value) => {
  const rounded = Math.round((Number(value) || 0) * 1000) / 1000;
  return rounded.toLocaleString(undefined, { maximumFractionDigits: 3 });
};

const normalizedUnit = (unit) => String(unit || 'unit').trim().toLowerCase();

export const quantityText = (quantity, unit) => (
  `${numberText(Math.abs(Number(quantity) || 0))} ${normalizedUnit(unit)}`
);

export const directionalMoneyText = (delta) => {
  if (closeToZero(delta)) return 'No meaningful change';
  return `${money(delta)} ${delta < 0 ? 'lower' : 'higher'}`;
};

export const directionalQuantityText = (delta, unit) => {
  if (Math.abs(Number(delta) || 0) < 1e-6) return 'Receiving the same quantity';
  return `Receiving ${quantityText(delta, unit)} ${delta < 0 ? 'less' : 'more'}`;
};

export const directionalComparableText = (delta, unit) => {
  if (delta === null || delta === undefined) return 'Comparable price unavailable';
  if (closeToZero(delta)) return `No meaningful change per ${normalizedUnit(unit)}`;
  return `${money(delta)} ${delta < 0 ? 'less' : 'more'} per ${normalizedUnit(unit)}`;
};

/**
 * Plain-English explanation assembled only from calculator results. This
 * function performs no package, price, fee, or PAR arithmetic of its own.
 */
export function explainReplacementImpact({
  vendorName = 'The replacement',
  purchaseSpendDelta = 0,
  quantityDelta = 0,
  quantityUnit = 'unit',
  comparablePriceDelta = null,
  comparisonUnit = null,
  coverage = null,
  needQuantity = null,
  needUnit = null,
  currentPackage = null,
  proposedPackage = null,
} = {}) {
  const sentences = [];
  const lessQuantity = Number(quantityDelta) < -1e-6;
  const moreQuantity = Number(quantityDelta) > 1e-6;
  const packageReason = currentPackage && proposedPackage && currentPackage !== proposedPackage
    ? ` because the package changes from ${currentPackage} to ${proposedPackage}`
    : '';

  if (closeToZero(purchaseSpendDelta)) {
    sentences.push('This change has no meaningful effect on purchase spending.');
  } else if (purchaseSpendDelta < 0 && lessQuantity && comparablePriceDelta > 0.004) {
    sentences.push(
      `This change lowers the purchase total by ${money(purchaseSpendDelta)} because you’re buying ${quantityText(quantityDelta, quantityUnit)} less.`,
    );
  } else if (purchaseSpendDelta > 0 && moreQuantity && comparablePriceDelta < -0.004) {
    sentences.push(
      `This change raises the purchase total by ${money(purchaseSpendDelta)} while delivering ${quantityText(quantityDelta, quantityUnit)} more.`,
    );
  } else {
    sentences.push(
      `${vendorName} ${purchaseSpendDelta < 0 ? 'lowers' : 'raises'} this purchase by ${money(purchaseSpendDelta)}.`,
    );
  }

  if (!(purchaseSpendDelta < 0 && lessQuantity && comparablePriceDelta > 0.004)) {
    if (lessQuantity) {
      sentences.push(`You’ll receive ${quantityText(quantityDelta, quantityUnit)} less${packageReason}.`);
    } else if (moreQuantity) {
      sentences.push(`You’ll receive ${quantityText(quantityDelta, quantityUnit)} more${packageReason}.`);
    } else {
      sentences.push('You’ll receive the same quantity.');
    }
  }

  if (coverage !== null && needQuantity !== null) {
    const need = `${numberText(needQuantity)} ${normalizedUnit(needUnit)}`;
    sentences.push(coverage
      ? `The proposed quantity still covers the ${need} need.`
      : `The proposed quantity no longer covers the ${need} need.`);
  }

  if (comparablePriceDelta !== null && comparisonUnit) {
    if (closeToZero(comparablePriceDelta)) {
      sentences.push(`The comparable price is effectively unchanged per ${normalizedUnit(comparisonUnit)}.`);
    } else {
      sentences.push(
        `On a comparable basis, the replacement costs ${money(comparablePriceDelta)} ${comparablePriceDelta < 0 ? 'less' : 'more'} per ${normalizedUnit(comparisonUnit)}.`,
      );
    }
  }

  return sentences.join(' ');
}

const pickupStops = (plan) => (plan?.vendorOrders || [])
  .filter((order) => order.isPickup && (order.itemCount || 0) > 0).length;

const planScore = (plan) => {
  const economic = buildPlanEconomics(plan);
  const blocking = (economic.summary?.unbuyableCount || 0)
    + (economic.summary?.packageRepairCount || 0);
  const overage = (economic.lines || []).reduce(
    (total, line) => total + Math.max(0, Number(line?.selected?.overage) || 0),
    0,
  );
  return [
    blocking,
    economic.landed.feasible ? 0 : economic.landed.infeasibleCount || 1,
    economic.landed.landedTotal,
    pickupStops(economic),
    economic.summary?.vendorOrderCount || 0,
    overage,
  ];
};

const scoreBefore = (left, right) => {
  for (let index = 0; index < left.length; index += 1) {
    if (left[index] < right[index]) return true;
    if (left[index] > right[index]) return false;
  }
  return false;
};

const withoutVendorAllowance = (allowances, vendorId) => {
  const result = {};
  Object.entries(allowances || {}).forEach(([lineId, ids]) => {
    const kept = (ids || []).map(String).filter((id) => id !== String(vendorId));
    if (kept.length) result[lineId] = kept;
  });
  return result;
};

/**
 * Proposes an excluded-vendor scenario by repeatedly evaluating each legal
 * replacement inside the complete landed plan. Package need, MOQ, conversion
 * safety, and equivalence eligibility remain owned by buildPurchasePlan;
 * landed fees/minimums remain owned by buildPlanEconomics.
 */
export function recommendVendorExclusionScenario(args = {}, vendorId) {
  const key = String(vendorId);
  const currentPlan = buildPurchasePlan(args);
  const affectedLines = currentPlan.lines.filter(
    (line) => line.vendorId != null && String(line.vendorId) === key,
  );
  const excludedVendorIds = [...new Set([...(args.excludedVendorIds || []).map(String), key])];
  const allowedExcludedVendorIdsByLineId = withoutVendorAllowance(
    args.allowedExcludedVendorIdsByLineId,
    key,
  );
  const proposedOptionByLineId = {};

  let proposedPlan = buildPurchasePlan({
    ...args,
    excludedVendorIds,
    allowedExcludedVendorIdsByLineId,
  });
  affectedLines.forEach((line) => {
    const proposed = proposedPlan.lines.find(
      (candidate) => String(candidate.lineId) === String(line.lineId),
    );
    if (proposed?.selectedOptionId) {
      proposedOptionByLineId[String(line.lineId)] = String(proposed.selectedOptionId);
    }
  });

  // Coordinate passes are bounded by the affected line count. Each accepted
  // choice strictly improves the deterministic score, so this always settles.
  for (let pass = 0; pass < Math.max(1, affectedLines.length); pass += 1) {
    let improved = false;
    affectedLines.forEach((affected) => {
      const draftLine = proposedPlan.lines.find(
        (line) => String(line.lineId) === String(affected.lineId),
      );
      const alternatives = (draftLine?.options || []).filter((row) => !row.isSubstitute);
      if (!alternatives.length) return;

      let winnerPlan = proposedPlan;
      let winnerOptionId = proposedOptionByLineId[String(affected.lineId)] || null;
      let winnerScore = planScore(proposedPlan);
      alternatives.forEach((row) => {
        const optionId = String(row.optionId);
        const candidateChoices = {
          ...proposedOptionByLineId,
          [String(affected.lineId)]: optionId,
        };
        const candidatePlan = buildPurchasePlan({
          ...args,
          explicitByLineId: { ...(args.explicitByLineId || {}), ...candidateChoices },
          excludedVendorIds,
          allowedExcludedVendorIdsByLineId,
        });
        const candidateScore = planScore(candidatePlan);
        if (scoreBefore(candidateScore, winnerScore)) {
          winnerPlan = candidatePlan;
          winnerOptionId = optionId;
          winnerScore = candidateScore;
        }
      });
      if (winnerPlan !== proposedPlan) {
        proposedPlan = winnerPlan;
        proposedOptionByLineId[String(affected.lineId)] = winnerOptionId;
        improved = true;
      }
    });
    if (!improved) break;
  }

  return {
    vendorId: key,
    currentPlan,
    proposedPlan,
    affectedLineIds: affectedLines.map((line) => String(line.lineId)),
    proposedOptionByLineId,
    excludedVendorIds,
    allowedExcludedVendorIdsByLineId,
  };
}

const orderByLineId = (plan) => {
  const result = new Map();
  (plan?.vendorOrders || []).forEach((order) => {
    (order.lines || []).forEach((line) => result.set(String(line.lineId), order));
  });
  return result;
};

const comparisonQuantityDelta = (from, to) => {
  const rawDelta = (finite(to?.selected?.quantityReceived) || 0)
    - (finite(from?.selected?.quantityReceived) || 0);
  const rawUnit = to?.countUnit || from?.countUnit || 'unit';
  const comparisonUnit = to?.comparisonUnit || from?.comparisonUnit || null;
  if (!comparisonUnit) return { value: round6(rawDelta), unit: rawUnit };
  // The shared conversion correctly rejects non-positive package quantities;
  // a difference can be negative, so convert its magnitude and restore the
  // direction afterwards.
  const convertedMagnitude = convertBasisQuantity(Math.abs(rawDelta), rawUnit, comparisonUnit);
  const converted = convertedMagnitude === null
    ? null
    : convertedMagnitude * (rawDelta < 0 ? -1 : 1);
  return converted === null
    ? { value: round6(rawDelta), unit: rawUnit }
    : { value: round6(converted), unit: comparisonUnit };
};

const uniqueAlternativeRows = (line, excludedVendorId) => {
  const byVendor = new Map();
  (line?.options || []).filter(
    (row) => !row.isSubstitute
      && String(row.option?.vendor_id ?? '') !== String(excludedVendorId),
  ).forEach((row) => {
    const vendorKey = String(row.option?.vendor_id ?? `option:${row.optionId}`);
    const existing = byVendor.get(vendorKey);
    if (!existing || (row.orderCost ?? Infinity) < (existing.orderCost ?? Infinity)) {
      byVendor.set(vendorKey, row);
    }
  });
  return [...byVendor.values()];
};

const operationalConsequences = (fromPlan, toPlan, toLine, vendorsById) => {
  if (!toLine?.selectedOptionId) return [];
  const vendorId = String(toLine.vendorId ?? '');
  const beforeOrder = (fromPlan.vendorOrders || []).find(
    (order) => String(order.vendorId ?? '') === vendorId,
  );
  const afterOrder = (toPlan.vendorOrders || []).find(
    (order) => String(order.vendorId ?? '') === vendorId,
  );
  if (!afterOrder) return [];
  const vendorName = vendorsById?.[vendorId]?.display_name || afterOrder.vendorName || 'The vendor';
  const consequences = [];
  if (!beforeOrder && afterOrder.isPickup) consequences.push('Adds a pickup stop');
  const beforeFees = beforeOrder
    ? (Number(beforeOrder.deliveryFee) || 0) + (Number(beforeOrder.fuelSurcharge) || 0)
      + (Number(beforeOrder.belowMinimumFee) || 0)
    : 0;
  const afterFees = (Number(afterOrder.deliveryFee) || 0) + (Number(afterOrder.fuelSurcharge) || 0)
    + (Number(afterOrder.belowMinimumFee) || 0);
  const feeDelta = Math.round((afterFees - beforeFees) * 100) / 100;
  if (feeDelta > 0.004) consequences.push(`Adds ${money(feeDelta)} in known fees`);
  if (beforeOrder?.minimumMet === false && afterOrder.minimumMet === true) {
    consequences.push(`Brings ${vendorName} above its minimum`);
  } else if (afterOrder.minimumMet === false) {
    consequences.push(`${vendorName} remains ${money(afterOrder.shortfall)} under its minimum`);
  }
  return consequences;
};

/** One display model for the item cards and final summary. */
export function buildVendorExclusionReviewModel({
  currentPlan,
  proposedPlan,
  vendorId,
  vendorsById = {},
} = {}) {
  const key = String(vendorId);
  const vendorName = vendorsById?.[key]?.display_name
    || currentPlan?.vendorOrders?.find((order) => String(order.vendorId) === key)?.vendorName
    || 'Vendor';
  const proposedById = new Map(
    (proposedPlan?.lines || []).map((line) => [String(line.lineId), line]),
  );
  const currentOrders = orderByLineId(currentPlan);
  const proposedOrders = orderByLineId(proposedPlan);
  const items = (currentPlan?.lines || [])
    .filter((line) => line.vendorId != null && String(line.vendorId) === key)
    .map((from) => {
      const to = proposedById.get(String(from.lineId)) || null;
      const alternatives = uniqueAlternativeRows(to, key);
      const quantity = comparisonQuantityDelta(from, to);
      const comparableFrom = finite(from?.selected?.comparableUnitCost);
      const comparableTo = finite(to?.selected?.comparableUnitCost);
      const comparisonUnit = to?.comparisonUnit || from?.comparisonUnit || null;
      const comparablePriceDelta = comparableFrom !== null && comparableTo !== null && comparisonUnit
        ? round6(comparableTo - comparableFrom)
        : null;
      const purchaseSpendDelta = Math.round(
        (((finite(to?.orderCost) || 0) - (finite(from?.orderCost) || 0)) * 100),
      ) / 100;
      const delivered = finite(to?.selected?.quantityReceived);
      const need = finite(to?.quantityNeeded ?? from?.quantityNeeded);
      const coverage = delivered === null || need === null ? null : delivered + 1e-6 >= need;
      const selectedVendorName = to?.vendorId != null
        ? vendorsById?.[String(to.vendorId)]?.display_name
          || proposedOrders.get(String(to.lineId))?.vendorName
          || 'Vendor'
        : null;
      const unresolved = !to?.selectedOptionId;
      return {
        lineId: String(from.lineId),
        inventoryItemId: from.inventoryItemId,
        description: from.description || to?.description || 'Item',
        status: alternatives.length === 0
          ? 'no_alternative'
          : alternatives.length === 1 ? 'one_alternative' : 'several_alternatives',
        currentVendorName: vendorName,
        proposedVendorName: selectedVendorName,
        proposedVendorId: to?.vendorId == null ? null : String(to.vendorId),
        selectedOptionId: to?.selectedOptionId == null ? null : String(to.selectedOptionId),
        alternatives: alternatives.map((row) => ({
          optionId: String(row.optionId),
          vendorId: row.option?.vendor_id == null ? null : String(row.option.vendor_id),
          vendorName: vendorsById?.[String(row.option?.vendor_id)]?.display_name
            || row.option?.vendor_name_text || 'Vendor',
          packageDescription: row.option?.pack_description || row.option?.purchase_unit || 'Package',
          orderCost: row.orderCost,
        })),
        currentPackage: from?.selected?.option?.pack_description
          || from?.selected?.option?.purchase_unit || 'Package not stated',
        proposedPackage: to?.selected?.option?.pack_description
          || to?.selected?.option?.purchase_unit || null,
        currentQuantityReceived: finite(from?.selected?.quantityReceived),
        proposedQuantityReceived: delivered,
        countUnit: to?.countUnit || from?.countUnit || 'unit',
        quantityDifference: quantity,
        currentOverage: finite(from?.selected?.overage),
        proposedOverage: finite(to?.selected?.overage),
        coverage,
        needQuantity: need,
        needUnit: to?.countUnit || from?.countUnit || 'unit',
        currentComparableUnitCost: comparableFrom,
        proposedComparableUnitCost: comparableTo,
        comparisonUnit,
        comparablePriceDelta,
        currentOrderCost: finite(from?.orderCost),
        proposedOrderCost: finite(to?.orderCost),
        purchaseSpendDelta,
        operationalConsequences: operationalConsequences(currentPlan, proposedPlan, to, vendorsById),
        explanation: alternatives.length === 0
          ? unresolved
            ? `No other vendor is available for ${from.description || 'this item'}. Excluding ${vendorName} will leave this item unpurchased.`
            : `No other vendor is available for ${from.description || 'this item'}. Keeping it with ${vendorName} means ${vendorName} will remain in this order.`
          : explainReplacementImpact({
            vendorName: selectedVendorName,
            purchaseSpendDelta,
            quantityDelta: quantity.value,
            quantityUnit: quantity.unit,
            comparablePriceDelta,
            comparisonUnit,
            coverage,
            needQuantity: need,
            needUnit: to?.countUnit || from?.countUnit || 'unit',
            currentPackage: from?.selected?.option?.pack_description || null,
            proposedPackage: to?.selected?.option?.pack_description || null,
          }),
        currentOrder: currentOrders.get(String(from.lineId)) || null,
        proposedOrder: proposedOrders.get(String(from.lineId)) || null,
      };
    });

  const comparison = comparePurchasePlans(currentPlan, proposedPlan);
  const movesByVendor = new Map();
  let unresolvedCount = 0;
  let keptCount = 0;
  items.forEach((item) => {
    if (!item.selectedOptionId) {
      unresolvedCount += 1;
    } else if (item.proposedVendorName === vendorName) {
      keptCount += 1;
    } else {
      movesByVendor.set(item.proposedVendorName, (movesByVendor.get(item.proposedVendorName) || 0) + 1);
    }
  });

  return {
    vendorId: key,
    vendorName,
    items,
    groups: {
      oneAlternative: items.filter((item) => item.status === 'one_alternative'),
      severalAlternatives: items.filter((item) => item.status === 'several_alternatives'),
      noAlternative: items.filter((item) => item.status === 'no_alternative'),
    },
    summary: {
      fullyExcluded: keptCount === 0,
      keptCount,
      unresolvedCount,
      moves: [...movesByVendor].map(([name, count]) => ({ vendorName: name, count })),
      purchaseTotalDelta: comparison.coveredProductSpendDelta,
      feeDelta: comparison.knownFeeDelta,
      landedDelta: comparison.landedDelta,
      economicImpactComplete: comparison.economicImpactComplete,
      pickupStopDelta: comparison.pickupStopDelta,
      comparison,
    },
  };
}
